import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, Search, Trash2, Users, X } from "lucide-react";
import { surveyApi, type DeptSummary, type Rep } from "@/lib/apiClient";
import AdminScreenTitle from "@/uniq/AdminScreenTitle";
import { T } from "@/uniq/theme";
import type { Assignment } from "@shared-domain/survey/contract";
import type { Customer } from "@shared-domain/lib/customer/provider";

const inp: React.CSSProperties = { width: "100%", border: "1px solid #E2E8F0", borderRadius: 8, padding: "8px 10px", fontSize: 14, boxSizing: "border-box" };

// 指名割当（顧客 × 担当）専用画面。一覧の割当アイコンから遷移する。
export default function SurveyAssign() {
  const { surveyId } = useParams();
  const [title, setTitle] = useState("");
  const [reps, setReps] = useState<Rep[]>([]);
  const [departments, setDepartments] = useState<DeptSummary[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [repId, setRepId] = useState("");
  // 部署は担当候補の絞込のみ (割当データには含めない)。"" = 指定なし = 全員。
  const [deptId, setDeptId] = useState("");
  const [custId, setCustId] = useState("");
  // ⑪ 顧客はフリーワードサジェストで選ぶ (顧客数が多く select では探せないため)。
  const [custQuery, setCustQuery] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cq = custQuery.trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (!cq) return [];
    const out: Customer[] = [];
    for (const c of customers) {
      if ([c.name, c.nameKana, c.no, c.addr].filter(Boolean).join(" ").toLowerCase().includes(cq)) {
        out.push(c);
        if (out.length >= 20) break;
      }
    }
    return out;
  }, [customers, cq]);

  const reload = () => {
    if (!surveyId) return;
    surveyApi.assignments(surveyId).then((r) => setAssignments(r.assignments)).catch(() => {});
  };
  useEffect(() => {
    if (!surveyId) return;
    surveyApi.get(surveyId).then((r) => setTitle(r.survey.title)).catch((e) => setError(String(e)));
    surveyApi.reps().then((r) => { setReps(r.reps); setDepartments(r.departments ?? []); }).catch(() => {});
    surveyApi.customers(surveyId).then((r) => setCustomers(r.customers)).catch(() => {});
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  // 1 顧客 = 1 担当 (回答は survey×顧客 で 1 件しか持てない)。既に別の担当へ割当済みなら
  // 確認のうえ付け替える。連続割当できるよう、担当・部署の選択は割当後も保持する。
  const add = async () => {
    if (!surveyId || !repId || !custId) return;
    const existing = assignments.filter((a) => a.customerId === custId);
    if (existing.some((a) => a.repUserId === repId)) return; // 同じ担当への重複割当は無害化
    if (existing.length > 0) {
      const holder = existing.map((a) => a.repUserName || a.repUserId).join("・");
      const next = reps.find((r) => r.userId === repId)?.name ?? "";
      if (!window.confirm(`${holder} さんに割り当てられています。担当を ${next} さんに変更しますか？`)) return;
      for (const a of existing) await surveyApi.unassign(surveyId, a.repUserId, custId);
    }
    await surveyApi.assign(surveyId, [{ repUserId: repId, customerId: custId }]);
    setCustId(""); setCustQuery("");
    await reload();
  };
  const pickCustomer = (c: Customer) => {
    setCustId(c.id);
    setCustQuery(c.name);
    setCustOpen(false);
  };
  const clearCustomer = () => {
    setCustId("");
    setCustQuery("");
    setCustOpen(false);
  };
  // 部署変更で現在の担当が候補外になったら選択を解除する。
  const changeDept = (v: string) => {
    setDeptId(v);
    if (v && repId && reps.find((r) => r.userId === repId)?.departmentId !== v) setRepId("");
  };
  const visibleReps = deptId ? reps.filter((r) => r.departmentId === deptId) : reps;

  return (
    <div style={{ minHeight: "100vh", background: T.bg }}>
      <div style={{ background: "#fff", borderBottom: `1px solid ${T.line}` }}>
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "14px 16px", display: "flex", alignItems: "center", gap: 10 }}>
          <Link to="/admin" style={{ display: "flex", alignItems: "center", gap: 4, color: T.sub, textDecoration: "none", fontSize: 13 }}><ArrowLeft size={16} />一覧</Link>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: "0 auto", padding: "16px" }}>
        <AdminScreenTitle icon={Users} title="指名割当" surveyName={title} />
        {error && <p style={{ color: "#dc2626", fontSize: 13 }}>{error}</p>}
        <div style={{ background: "#fff", borderRadius: 12, padding: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <label style={{ fontSize: 12, color: T.sub, position: "relative", display: "block" }}>顧客<br />
              <span style={{ position: "relative", display: "block", marginTop: 4 }}>
                <Search size={14} color={T.faint} style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                <input
                  style={{ ...inp, borderRadius: 9999, paddingLeft: 34, paddingRight: 34, borderColor: custId ? T.accent : "#E2E8F0" }}
                  value={custQuery}
                  placeholder="顧客名・番号・住所で検索"
                  onChange={(e) => { setCustQuery(e.target.value); setCustId(""); setCustOpen(true); }}
                  onFocus={() => { if (!custId) setCustOpen(true); }}
                  onBlur={() => setTimeout(() => setCustOpen(false), 150)}
                />
                {custQuery && (
                  <button onClick={clearCustomer} title="クリア" aria-label="クリア" style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", width: 24, height: 24, borderRadius: 9999, background: "transparent", border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={14} color={T.faint} /></button>
                )}
                {custOpen && cq && (
                  // パネルの見た目は uniq/CompanyAutocomplete のサジェストパネルと同値に揃える。
                  <div style={{ position: "absolute", zIndex: 40, top: "100%", left: 0, marginTop: 4, width: "100%", maxHeight: 260, overflow: "auto", background: "#fff", border: "1px solid #E8EDF3", borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,.14)" }}>
                    {suggestions.map((c) => (
                      <button key={c.id} onMouseDown={(e) => { e.preventDefault(); pickCustomer(c); }}
                        style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: 0, borderBottom: "1px solid #F1F5F9", padding: "9px 12px", cursor: "pointer" }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{c.name}</span>
                        {(c.no || c.addr) && <span style={{ display: "block", fontSize: 11, color: T.faint, marginTop: 1 }}>{c.no}{c.no && c.addr ? " ・ " : ""}{c.addr}</span>}
                      </button>
                    ))}
                    {suggestions.length === 0 && <div style={{ padding: "10px 12px", fontSize: 12, color: T.faint }}>該当する顧客がありません</div>}
                  </div>
                )}
              </span>
            </label>
            {departments.length > 0 && (
              <label style={{ fontSize: 12, color: T.sub, display: "block" }}>部署<br /><select style={{ ...inp, marginTop: 4 }} value={deptId} onChange={(e) => changeDept(e.target.value)}><option value="">指定なし</option>{departments.map((d) => <option key={d.deptId} value={d.deptId}>{d.name}</option>)}</select></label>
            )}
            <label style={{ fontSize: 12, color: T.sub, display: "block" }}>担当<br /><select style={{ ...inp, marginTop: 4 }} value={repId} onChange={(e) => setRepId(e.target.value)}><option value="">選択…</option>{visibleReps.map((r) => <option key={r.userId} value={r.userId}>{r.name}</option>)}</select></label>
            <button onClick={add} disabled={!repId || !custId} style={{ alignSelf: "center", background: repId && custId ? T.accent : "#D8E0EA", color: "#fff", border: 0, borderRadius: 9999, padding: "10px 32px", fontSize: 13, fontWeight: 700, cursor: repId && custId ? "pointer" : "not-allowed" }}>割当</button>
          </div>
          <ul style={{ marginTop: 14, padding: 0, listStyle: "none", fontSize: 13 }}>
            {assignments.map((a) => (
              <li key={`${a.repUserId}#${a.customerId}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderTop: "1px solid #F1F5F9" }}>
                <span style={{ fontWeight: 600 }}>{a.customerName || a.customerId}</span><span style={{ color: T.faint }}>→</span><span>{a.repUserName || a.repUserId}</span>
                <button onClick={async () => { if (!surveyId) return; await surveyApi.unassign(surveyId, a.repUserId, a.customerId); reload(); }} style={{ marginLeft: "auto", background: "transparent", border: 0, cursor: "pointer", color: "#CBD5E1" }}><Trash2 size={14} /></button>
              </li>
            ))}
            {assignments.length === 0 && <li style={{ color: T.faint, padding: "8px 0" }}>まだ割当がありません。</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
